// src/pages/FoundationDashboard.jsx
//
// Dashboard pour directeur de fondation (foundation_mode = true).
// 3 onglets collections : Propriété propre / Donations & Legs / Dépôts & Prêts
// + vue prêts sortants (artworks en loan_status = 'on_loan')
// + accès contrats depuis la table deposits
//
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-hot-toast";
import {
  Building2, FileText, Calendar, X, Users,
} from "lucide-react";
import CollaboratorsManager from "../components/CollaboratorsManager";
import SharedAccessSection from "../components/SharedAccessSection";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
  n ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n) : "—";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
}

const COLLECTION_TYPES = {
  foundation_owned:    { label: "Propriété propre",     color: "#2c5f4a", bg: "#edf4f0" },
  foundation_founder:  { label: "Prêt fondateur",       color: "#7a5c00", bg: "#fdf8ec" },
  foundation_donation: { label: "Donation / legs",      color: "#8b4513", bg: "#faf0e6" },
  foundation_deposit:  { label: "Dépôts & Prêts reçus", color: "#1c3a5e", bg: "#eef2f8" },
};

const LEGAL_STATUS_LABELS = {
  fondation_abritee:          "Fondation abritée (Fondation de France)",
  fondation_reconnue:         "Fondation reconnue d'utilité publique",
  don_avec_reserve_usufruit:  "Don avec réserve d'usufruit",
  don_legs_inalienable:       "Don / legs — inaliénable",
  pret_artiste_vivant:        "Prêt artiste vivant",
  pret_institutionnel:        "Prêt institutionnel",
};

const OWNER_TYPE_LABELS = {
  private_collector: "Collectionneur privé",
  institution:       "Institution",
  family_succession: "Famille / succession",
  a_clarifier:       "À clarifier",
};

function ContractBadge({ endDate }) {
  const days = daysUntil(endDate);
  if (days === null)
    return <span style={sBadge("#d97706", "#fffbeb", "#fde68a")}>⚠ Pas de date de fin</span>;
  if (days < 0)
    return <span style={sBadge("#dc2626", "#fef2f2", "#fecaca")}>EXPIRÉ il y a {Math.abs(days)}j</span>;
  if (days <= 60)
    return <span style={sBadge("#d97706", "#fffbeb", "#fde68a")}>⚠ Expire dans {days}j</span>;
  return <span style={{ fontSize: 11, color: "#16a34a", fontFamily: "system-ui" }}>Valide jusqu'au {fmtDate(endDate)}</span>;
}

function sBadge(color, bg, border) {
  return { fontSize: 11, color, background: bg, border: `1px solid ${border}`,
    padding: "2px 8px", borderRadius: 3, fontFamily: "system-ui", fontWeight: 700 };
}

// ─── Carte collection ─────────────────────────────────────────────────────────

function CollectionCard({ col }) {
  const type = COLLECTION_TYPES[col.collection_type] || { label: col.collection_type, color: "#888", bg: "#f5f5f5" };
  const dep = col.deposit;
  const isDeposit = col.collection_type === "foundation_deposit";

  return (
    <div style={{
      border: "1px solid #e0d8cc", borderRadius: 8,
      background: "#fff", overflow: "hidden", marginBottom: 12,
    }}>
      <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "Georgia, serif", fontSize: 16, color: "#1a1a1a" }}>{col.title}</span>
            <span style={{
              fontSize: 10, fontFamily: "system-ui", fontWeight: 700, padding: "2px 7px",
              borderRadius: 2, textTransform: "uppercase",
              background: type.color, color: "#fff",
            }}>
              {type.label}
            </span>
          </div>
          <div style={{ fontSize: 12, color: "#888", fontFamily: "system-ui" }}>
            {col.artworkCount} œuvre{col.artworkCount !== 1 ? "s" : ""}
            {col.totalValue > 0 && <span> · {fmt(col.totalValue)}</span>}
          </div>
          {col.legal_status && (
            <div style={{ fontSize: 11, color: type.color, fontFamily: "system-ui", marginTop: 3 }}>
              {LEGAL_STATUS_LABELS[col.legal_status] || col.legal_status}
            </div>
          )}
          {col.legal_notes && (
            <div style={{ fontSize: 11, color: "#aaa", fontFamily: "Georgia, serif", fontStyle: "italic", marginTop: 4, maxWidth: 520 }}>
              {col.legal_notes}
            </div>
          )}
        </div>
        <Link to={`/collections/${col.id}`} style={{
          fontSize: 12, color: "#c9a961", fontFamily: "system-ui",
          fontWeight: 600, textDecoration: "none", marginLeft: 16, flexShrink: 0,
        }}>
          Voir →
        </Link>
      </div>

      {/* Fiche dépôt si type foundation_deposit */}
      {isDeposit && (
        <div style={{ padding: "12px 20px", background: "#f8f6f2", borderTop: "1px solid #f0ece5" }}>
          {dep ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center" }}>
              {dep.owner_name && (
                <span style={{ fontSize: 12, color: "#555", fontFamily: "system-ui", display: "flex", alignItems: "center", gap: 5 }}>
                  <Building2 size={12} color="#aaa" />
                  {OWNER_TYPE_LABELS[dep.owner_type] || dep.owner_type} — {dep.owner_name}
                </span>
              )}
              {dep.contract_start_date && (
                <span style={{ fontSize: 12, color: "#555", fontFamily: "system-ui", display: "flex", alignItems: "center", gap: 5 }}>
                  <Calendar size={12} color="#aaa" />
                  {fmtDate(dep.contract_start_date)} → {fmtDate(dep.contract_end_date)}
                </span>
              )}
              <ContractBadge endDate={dep.contract_end_date} />
              {dep.contract_file_url && (
                <a href={dep.contract_file_url} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 12, color: "#1c3a5e", fontFamily: "system-ui", fontWeight: 600,
                    textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
                  <FileText size={12} /> Contrat
                </a>
              )}
              {dep.insurance_required && (
                <span style={{ fontSize: 12, color: "#555", fontFamily: "system-ui" }}>
                  Assurance : {dep.insurance_holder || "à définir"}
                </span>
              )}
            </div>
          ) : (
            <span style={{ fontSize: 12, color: "#92400e", fontFamily: "system-ui" }}>
              ⚠ Aucune fiche dépôt — contrat et propriétaire inconnus
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Carte prêt sortant ───────────────────────────────────────────────────────

function LoanOutCard({ artwork }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "12px 16px", background: "#fff",
      border: "1px solid #e0d8cc", borderRadius: 6, marginBottom: 8,
    }}>
      <div style={{ flex: 1 }}>
        <span style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1a1a1a" }}>
          {artwork.title || "Sans titre"}
        </span>
        {artwork.artist && (
          <span style={{ fontSize: 13, color: "#888", marginLeft: 8 }}>— {artwork.artist}</span>
        )}
        {artwork.inventory_number && (
          <span style={{ fontSize: 11, color: "#bbb", marginLeft: 8 }}>[{artwork.inventory_number}]</span>
        )}
        {artwork.storage_location_detail && (
          <div style={{ fontSize: 12, color: "#1c3a5e", fontFamily: "system-ui", marginTop: 2 }}>
            ↗ {artwork.storage_location_detail}
          </div>
        )}
        {artwork.collections?.title && (
          <div style={{ fontSize: 11, color: "#c9a961", fontFamily: "system-ui", marginTop: 1 }}>
            Collection : {artwork.collections.title}
          </div>
        )}
      </div>
      <Link to={`/artwork/${artwork.id}`} style={{
        fontSize: 12, color: "#888", fontFamily: "system-ui", textDecoration: "none", flexShrink: 0, marginLeft: 12,
      }}>
        Fiche →
      </Link>
    </div>
  );
}

// ─── Indicateur résumé ────────────────────────────────────────────────────────

function StatBoxContrats({ count, list, onNavigate }) {
  const [open, setOpen] = React.useState(false);
  const color = count > 0 ? "#dc2626" : "#16a34a";
  return (
    <div style={{ flex: "1 1 160px", position: "relative" }}>
      <button
        onClick={() => { if (count > 0) { setOpen((v) => !v); onNavigate(); } }}
        style={{
          width: "100%", textAlign: "left", background: "#fff",
          border: count > 0 ? "1px solid #fecaca" : "1px solid #e0d8cc",
          borderRadius: 8, padding: "16px 20px", cursor: count > 0 ? "pointer" : "default",
        }}
      >
        <div style={{ fontSize: 22, fontFamily: "Georgia, serif", color, marginBottom: 4 }}>{count}</div>
        <div style={{ fontSize: 12, color: "#888", fontFamily: "system-ui" }}>Contrats à renouveler</div>
        <div style={{ fontSize: 11, color: count > 0 ? "#dc2626" : "#aaa", fontFamily: "system-ui", marginTop: 2 }}>
          {count > 0 ? "dans les 60 prochains jours ↓" : "RAS"}
        </div>
      </button>
      {open && count > 0 && (
        <div style={{
          position: "absolute", top: "110%", left: 0, zIndex: 50, minWidth: 280,
          background: "#fff", border: "1px solid #e0d8cc", borderRadius: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.10)", padding: "12px 16px",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#888", fontFamily: "system-ui", marginBottom: 8, textTransform: "uppercase" }}>
            Collections concernées
          </div>
          {list.map((c) => {
            const days = daysUntil(c.deposit?.contract_end_date);
            return (
              <div key={c.id} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid #f0ece5" }}>
                <div style={{ fontSize: 13, fontFamily: "Georgia, serif", color: "#1a1a1a" }}>{c.title}</div>
                <div style={{ fontSize: 11, color: "#aaa", fontFamily: "system-ui", marginTop: 2 }}>
                  {c.deposit?.owner_name || "Propriétaire inconnu"}
                  {" · "}
                  {days === null
                    ? "⚠ Pas de contrat renseigné"
                    : days < 0
                    ? `⚠ Expiré il y a ${Math.abs(days)} j`
                    : `Expire dans ${days} j`}
                </div>
              </div>
            );
          })}
          <button onClick={() => setOpen(false)} style={{ fontSize: 11, color: "#888", background: "none", border: "none", cursor: "pointer", marginTop: 4 }}>
            Fermer
          </button>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, sub, color }) {
  return (
    <div style={{
      flex: "1 1 160px", background: "#fff", border: "1px solid #e0d8cc",
      borderRadius: 8, padding: "16px 20px",
    }}>
      <div style={{ fontSize: 22, fontFamily: "Georgia, serif", color: color || "#1a1a1a", marginBottom: 4 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: "#888", fontFamily: "system-ui" }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "#aaa", fontFamily: "system-ui", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function FoundationDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [collections, setCollections] = useState([]);
  const [loansOut, setLoansOut] = useState([]);
  const [tab, setTab] = useState("owned");
  const [collabDrawerOpen, setCollabDrawerOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const uid = user?.id;
      if (!uid) return;

      // 1. Toutes les collections foundation_* de l'utilisateur
      const { data: cols, error: colsErr } = await supabase
        .from("collections")
        .select("id, title, collection_type, legal_status, legal_notes")
        .eq("client_id", uid)
        .in("collection_type", ["foundation_owned", "foundation_founder", "foundation_donation", "foundation_deposit"]);

      if (colsErr) throw colsErr;
      const colsData = cols || [];

      // 2. Compter œuvres + valeur par collection
      const colIds = colsData.map((c) => c.id);
      let countMap = {};
      let valueMap = {};
      if (colIds.length) {
        for (const col of colsData) {
          const { data: aw } = await supabase
            .from("artworks")
            .select("current_estimated_value")
            .eq("collection_id", col.id)
            .or("availability.is.null,availability.neq.sold");
          countMap[col.id] = aw?.length || 0;
          valueMap[col.id] = (aw || []).reduce((s, a) => s + (Number(a.current_estimated_value) || 0), 0);
        }

        // 3. Fiches deposits pour les collections foundation_deposit
        const depositIds = colsData.filter((c) => c.collection_type === "foundation_deposit").map((c) => c.id);
        let depositMap = {};
        if (depositIds.length) {
          const { data: deps } = await supabase
            .from("deposits")
            .select("*")
            .in("collection_id", depositIds);
          (deps || []).forEach((d) => { depositMap[d.collection_id] = d; });
        }

        setCollections(colsData.map((c) => ({
          ...c,
          artworkCount: countMap[c.id] || 0,
          totalValue: valueMap[c.id] || 0,
          deposit: depositMap[c.id] || null,
        })));
      } else {
        setCollections([]);
      }

      // 4. Prêts sortants — artworks de ces collections marqués on_loan
      if (colIds.length) {
        const { data: loans } = await supabase
          .from("artworks")
          .select("id, title, artist, inventory_number, storage_location_detail, loan_status, availability, collections(title)")
          .eq("client_id", uid)
          .or("loan_status.eq.on_loan,availability.eq.on_loan")
          .in("collection_id", colIds);
        setLoansOut(loans || []);
      } else {
        setLoansOut([]);
      }
    } catch (err) {
      toast.error("Erreur chargement : " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [user?.id]);

  // Stats globales
  const totalArtworks = useMemo(() => collections.reduce((s, c) => s + c.artworkCount, 0), [collections]);
  const totalValue = useMemo(() => collections.reduce((s, c) => s + c.totalValue, 0), [collections]);
  const expiringDepositsList = useMemo(() => {
    return collections.filter((c) => {
      if (c.collection_type !== "foundation_deposit") return false;
      const days = daysUntil(c.deposit?.contract_end_date);
      return days === null || (days !== null && days <= 60);
    });
  }, [collections]);
  const expiringDeposits = expiringDepositsList.length;

  // Filtrage par onglet
  const TABS = [
    { key: "owned",    label: "Propriété propre",     types: ["foundation_owned", "foundation_founder"] },
    { key: "donation", label: "Donations & Legs",      types: ["foundation_donation"] },
    { key: "deposit",  label: "Dépôts & Prêts reçus", types: ["foundation_deposit"] },
    { key: "loans",    label: "Prêts sortants",        types: null },
  ];

  const activeTab = TABS.find((t) => t.key === tab);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleCollections = useMemo(() => {
    if (!activeTab?.types) return [];
    return collections.filter((c) => activeTab.types.includes(c.collection_type));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collections, tab]);

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "40px 24px" }}>

      {/* ── En-tête ── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontFamily: "Georgia, serif", fontSize: 28, color: "#1a1a1a", margin: "0 0 4px" }}>
              Tableau de bord Fondation
            </h1>
            <p style={{ fontSize: 13, color: "#888", fontFamily: "system-ui", margin: 0 }}>
              Gestion des collections et des contrats de dépôt
            </p>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <Link to="/foundation-report" style={sBtn("#1a1a1a", "#fff")}>
              Rapport annuel
            </Link>
            <button type="button" onClick={() => setCollabDrawerOpen(true)} style={{ ...sBtn("#fff", "#1a1a1a", "#e0d8cc"), display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Users size={14} /> Collaborateurs
            </button>
            <Link to="/deposits" style={sBtn("#fff", "#1a1a1a", "#e0d8cc")}>
              Dépôts & Prêts →
            </Link>
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      {!loading && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 32 }}>
          <StatBox label="Collections" value={collections.length} />
          <StatBox label="Œuvres actives" value={totalArtworks} />
          <StatBox label="Valeur estimée" value={totalValue > 0 ? fmt(totalValue) : "—"} />
          <StatBoxContrats
            count={expiringDeposits}
            list={expiringDepositsList}
            onNavigate={() => setTab("deposit")}
          />
          <StatBox label="Prêts sortants" value={loansOut.length} />
        </div>
      )}

      {/* ── Onglets ── */}
      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #e0d8cc", marginBottom: 28 }}>
        {TABS.map((t) => {
          const count = t.types
            ? collections.filter((c) => t.types.includes(c.collection_type)).length
            : loansOut.length;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: "10px 18px", background: "none", border: "none",
                borderBottom: tab === t.key ? "2px solid #1a1a1a" : "2px solid transparent",
                cursor: "pointer", fontSize: 13, fontFamily: "system-ui",
                color: tab === t.key ? "#1a1a1a" : "#888",
                fontWeight: tab === t.key ? 600 : 400,
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              {t.label}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10,
                background: "#f0ece5", color: "#888", border: "1px solid #e0d8cc",
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Contenu ── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#aaa", fontFamily: "Georgia, serif" }}>
          Chargement…
        </div>
      ) : (
        <>
          {/* Collections */}
          {tab !== "loans" && (
            visibleCollections.length === 0 ? (
              <EmptyState
                icon="◇"
                title={`Aucune collection « ${activeTab?.label} »`}
                text="Créez une collection avec le type fondation correspondant pour la gérer ici."
                cta="Mes collections"
                to="/collections"
              />
            ) : (
              visibleCollections.map((col) => <CollectionCard key={col.id} col={col} />)
            )
          )}

          {/* Prêts sortants */}
          {tab === "loans" && (
            loansOut.length === 0 ? (
              <EmptyState
                icon="↗"
                title="Aucun prêt sortant"
                text="Marquez des œuvres comme « en prêt » depuis leurs fiches individuelles."
                cta="Voir les collections"
                to="/collections"
              />
            ) : (
              <>
                <div style={{
                  padding: "12px 16px", background: "#eef2f8", border: "1px solid #c5d4e8",
                  borderRadius: 6, marginBottom: 16, fontSize: 13, color: "#1c3a5e", fontFamily: "system-ui",
                }}>
                  Ces œuvres sont actuellement prêtées à des institutions tierces.
                  Pensez à vérifier les dates de retour directement sur chaque fiche.
                </div>
                {loansOut.map((a) => <LoanOutCard key={a.id} artwork={a} />)}
              </>
            )
          )}
        </>
      )}

      {/* ── Drawer Collaborateurs ── */}
      {collabDrawerOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex" }}>
          <button
            type="button"
            aria-label="Fermer"
            onClick={() => setCollabDrawerOpen(false)}
            style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", cursor: "pointer", border: "none" }}
          />
          <div style={{
            position: "relative", marginLeft: "auto",
            width: "min(540px, 100vw)",
            background: "#fff", height: "100%", overflowY: "auto", padding: "28px 24px",
            display: "flex", flexDirection: "column", gap: 24,
            boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Users size={20} />
                <span style={{ fontFamily: "Georgia, serif", fontSize: 18 }}>Collaborateurs</span>
              </div>
              <button type="button" onClick={() => setCollabDrawerOpen(false)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                <X size={20} />
              </button>
            </div>
            <CollaboratorsManager
              userId={user?.id}
              userPlan="fondation"
              collectionId={null}
            />
            <SharedAccessSection collectionId={null} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sous-composants utilitaires ──────────────────────────────────────────────

function EmptyState({ icon, title, text, cta, to }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 24px" }}>
      <div style={{ fontFamily: "Georgia, serif", fontSize: 36, color: "#e0d8cc", marginBottom: 16 }}>{icon}</div>
      <p style={{ fontFamily: "Georgia, serif", fontSize: 17, color: "#1a1a1a", marginBottom: 8 }}>{title}</p>
      <p style={{ fontSize: 13, color: "#888", fontFamily: "system-ui", marginBottom: 20, maxWidth: 360, margin: "0 auto 20px" }}>{text}</p>
      {cta && to && (
        <Link to={to} style={{
          display: "inline-block", padding: "9px 20px", background: "#1a1a1a", color: "#fff",
          fontSize: 13, fontFamily: "system-ui", fontWeight: 600, textDecoration: "none", borderRadius: 4,
        }}>
          {cta}
        </Link>
      )}
    </div>
  );
}

function sBtn(bg, color, border) {
  return {
    display: "inline-block", padding: "8px 16px",
    background: bg, color,
    border: `1px solid ${border || bg}`,
    fontSize: 12, fontFamily: "system-ui", fontWeight: 600,
    textDecoration: "none", borderRadius: 4, cursor: "pointer",
  };
}
